import { IconName } from '@optimus-web/icons';
import { ILocalizationContext, OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { ensureString } from '../utils';
import { Stack, StackItem } from '../Stack';
import { Typography, TypographyOptions } from '../typography';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Container } from '../Container';
import { BannerElement, LinkContainer } from './style';
import { BannerVariant } from './types';

const DEFAULT_ICONS: Record<BannerVariant, IconName> = {
    info: IconName.Info,
    success: IconName.CheckCircle,
    warning: IconName.Problematic,
    error: IconName.AlertCircle,
};

export type BannerProps = React.HTMLAttributes<HTMLElement> & {
    variant: BannerVariant;
    centered?: boolean;
    onClose?: (event: React.MouseEvent<HTMLElement>) => void;
    action?: React.ReactNode;
    wordBreak?: TypographyOptions['wordBreak'];
    headingLevel?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
} & (
        | { primaryText: React.ReactNode; secondaryText?: React.ReactNode }
        | { primaryText?: React.ReactNode; secondaryText: React.ReactNode }
    );

const getStatusLabel = (variant: BannerVariant, localize: ILocalizationContext['t']) => {
    switch (variant) {
        case 'info':
            return localize(OptimusTextKey.Information);
        case 'success':
            return localize(OptimusTextKey.Success);
        case 'warning':
            return localize(OptimusTextKey.Warning);
        case 'error':
            return localize(OptimusTextKey.Error);
    }
};

const Banner = ({
    primaryText,
    variant = 'info',
    centered,
    onClose,
    action,
    secondaryText,
    wordBreak,
    headingLevel = 'h2',
    ...other
}: BannerProps) => {
    const { t } = useLocalization();

    return (
        <BannerElement variant={variant} aria-label={ensureString({ value: primaryText })} spacing="50" {...other}>
            <StackItem grow>
                <Container padding={['50', '0', '0', '0']}>
                    <Stack spacing="150">
                        <Icon value={DEFAULT_ICONS[variant]} alt={getStatusLabel(variant, t)} />
                        <Stack vertical fill spacing="50">
                            {primaryText ? (
                                <Typography
                                    textStyle="bodyMediumStrong"
                                    wordBreak={wordBreak}
                                    align={centered ? 'center' : 'left'}
                                    as={headingLevel}
                                >
                                    {primaryText}
                                </Typography>
                            ) : null}
                            {secondaryText ? (
                                <Typography
                                    textStyle="bodyMedium"
                                    as="div"
                                    wordBreak={wordBreak}
                                    align={centered ? 'center' : 'left'}
                                >
                                    {secondaryText}
                                </Typography>
                            ) : null}
                            {action ? (
                                <LinkContainer padding={['50', '0', '0']} align={centered ? 'center' : 'left'}>
                                    {action}
                                </LinkContainer>
                            ) : null}
                        </Stack>
                    </Stack>
                </Container>
            </StackItem>
            {onClose ? (
                <Container position="relative">
                    <IconButton
                        onClick={onClose}
                        size="small"
                        icon={IconName.CrossClose}
                        aria-label={ensureString({ value: t(OptimusTextKey.CloseBanner) })}
                        variant="ghost"
                        type="button"
                    />
                </Container>
            ) : null}
        </BannerElement>
    );
};

export default Banner;
